import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {useCreateProductionTemplate} from '../../../hooks/productions/useProductions';
import {CreateProductionTemplatePayload} from '../../../api/types/production.type';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';

const ProductionTemplateApiTest: React.FC = () => {
  const [field, setField] = useState<string>('Template From API Demo');
  const [productionId, setProductionId] = useState<number>(16);
  const [productionSetId, setProductionSetId] = useState<number>(1);
  const [securityGrpId, setSecurityGrpId] = useState<number>(1);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const createTemplateMutation = useCreateProductionTemplate(
    (data) => {
      console.log('Template created successfully:', data);
    },
    (error) => {
      console.error('Error creating template:', error);
    }
  );

  const handleTestApi = () => {
    const payload: CreateProductionTemplatePayload = {
      field,
      productionId,
      productionSetId,
      securityGrpId,
    };
    createTemplateMutation.mutate(payload);
  };

  const handleReset = () => {
    setField('Template From API Demo');
    setProductionId(16);
    setProductionSetId(1);
    setSecurityGrpId(1);
  };

  const handleCopyJson = async () => {
    const payload = {
      field,
      productionId,
      productionSetId,
      securityGrpId,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyResponse = async () => {
    if (createTemplateMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(createTemplateMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Create Production Template API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Create a custom production template from an existing production
      </Typography>

      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Payload
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Template Field"
            type="text"
            value={field}
            onChange={(e) => setField(e.target.value)}
            size="small"
            placeholder="Enter template field"
            sx={{minWidth: '200px'}}
          />
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
            placeholder="Enter production ID"
          />
          <TextField
            label="Production Set ID"
            type="number"
            value={productionSetId}
            onChange={(e) => setProductionSetId(Number(e.target.value))}
            size="small"
            placeholder="Enter production set ID"
          />
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGrpId}
            onChange={(e) => setSecurityGrpId(Number(e.target.value))}
            size="small"
            placeholder="Enter security group ID"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            disabled={createTemplateMutation.isPending}
          >
            {createTemplateMutation.isPending
              ? '⏳ Creating...'
              : '🚀 Create Template'}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            🔄 Reset
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {createTemplateMutation.isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Creating template... Making API request...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {createTemplateMutation.error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {createTemplateMutation.error.message}
          </Typography>
        </Box>
      )}

      {/* Success State - Display Response */}
      {createTemplateMutation.data && !createTemplateMutation.isPending && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* Success Message */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size" gutterBottom>
              🎉 Template Created Successfully!
            </Typography>
            <Typography className="typo-text body3_size">
              {createTemplateMutation.data.message}
            </Typography>
          </Box>

          {/* Expandable Sections */}
          <Box sx={{marginTop: 2}}>
            {/* Request Payload */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📤 Request Payload
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyJson}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Payload'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    {
                      field,
                      productionId,
                      productionSetId,
                      securityGrpId,
                    },
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Response Data */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📥 Response Data
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyResponse}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Response'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(createTemplateMutation.data, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Full Response */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📄 Full JSON Response
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: '#263238',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '500px',
                  }}
                >
                  <pre
                    style={{
                      color: '#4caf50',
                      fontSize: '12px',
                      margin: 0,
                      fontFamily: 'monospace',
                    }}
                  >
                    {JSON.stringify(createTemplateMutation.data, null, 2)}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      )}

      {/* Initial State */}
      {!createTemplateMutation.isPending &&
        !createTemplateMutation.data &&
        !createTemplateMutation.error && (
          <Box
            sx={{
              padding: 3,
              backgroundColor: '#fff3e0',
              borderRadius: 1,
              border: '1px solid #ff9800',
            }}
          >
            <Typography className="typo-text body2_size">
              👆 Click "Create Template" button to make the API request
            </Typography>
          </Box>
        )}

      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong> /productions/api/v1/production/templates
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> POST
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Content-Type:</strong> application/json
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Request Body:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`{
  "field": "${field}",
  "productionId": ${productionId},
  "productionSetId": ${productionSetId},
  "securityGrpId": ${securityGrpId}
}`}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Expected Response:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`{
  "message": "Production Saved as a Custom Template."
}`}
        </pre>
      </Box>

      {/* Hook Usage Example */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#f0f8ff'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          💻 Hook Usage Example
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {`import { useCreateProductionTemplate } from '../../../hooks/productions/useProductions';

const MyComponent = () => {
  const createTemplate = useCreateProductionTemplate(
    (data) => console.log('Success:', data),
    (error) => console.error('Error:', error)
  );

  const handleCreate = () => {
    createTemplate.mutate({
      field: 'Template Name',
      productionId: 16,
      productionSetId: 1,
      securityGrpId: 1
    });
  };

  return (
    <button 
      onClick={handleCreate}
      disabled={createTemplate.isPending}
    >
      {createTemplate.isPending ? 'Creating...' : 'Create Template'}
    </button>
  );
};`}
        </pre>
      </Box>
    </Box>
  );
};

export default ProductionTemplateApiTest;
